/**
 * Planned workouts tab — the user's upcoming schedule.
 *
 * Shows every workout session whose status is `planned` or `in_progress`,
 * ordered from soonest → furthest out. The in-progress session (if any)
 * is pinned at the top with an "In progress" pill so the user always sees
 * it before the future schedule.
 *
 * Clicking a row delegates back to the parent, which routes the user to
 * the Today tab with the chosen session pinned — so the full Tracker
 * (with editable sets, prev hints, agent notes) opens, not the read-only
 * History summary.
 */

import { useMemo, useState } from 'react';
import { Icon } from '@k-os/ui';
import {
  useWorkouts,
  type ExerciseType,
  type WorkoutSessionDto,
} from '../../../api/health';
import { EXERCISE_TYPE_META, StatusPill, TypeChip } from './atoms';
import styles from './workouts.module.css';

const TYPE_KEYS = Object.keys(EXERCISE_TYPE_META) as ExerciseType[];

export function WorkoutPlanned({
  onOpen,
}: {
  onOpen: (w: WorkoutSessionDto) => void;
}) {
  const workoutsQ = useWorkouts();
  const [typeFilter, setTypeFilter] = useState<ExerciseType | 'all'>('all');
  const [q, setQ] = useState('');

  const items = useMemo(() => {
    const all = workoutsQ.data?.workouts ?? [];
    const planned = all.filter(
      (w) => w.status === 'planned' || w.status === 'in_progress',
    );
    // Sort: in_progress first, then planned ascending by date (soonest next).
    return planned.sort((a, b) => {
      if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
      if (b.status === 'in_progress' && a.status !== 'in_progress') return 1;
      return a.date.localeCompare(b.date);
    });
  }, [workoutsQ.data]);

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return items.filter((w) => {
      if (typeFilter !== 'all' && w.type !== typeFilter) return false;
      if (lower && !w.name.toLowerCase().includes(lower)) return false;
      return true;
    });
  }, [items, typeFilter, q]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search planned…"
          className={styles.searchField}
        />
        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          {filtered.length} of {items.length}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 12,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginRight: 4,
          }}
        >
          Type
        </span>
        <button
          type="button"
          className={`${styles.filterChip} ${typeFilter === 'all' ? styles.filterChipActive : ''}`}
          onClick={() => setTypeFilter('all')}
        >
          All
        </button>
        {TYPE_KEYS.map((t) => (
          <button
            key={t}
            type="button"
            className={`${styles.filterChip} ${typeFilter === t ? styles.filterChipActive : ''}`}
            onClick={() => setTypeFilter(t)}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                background: EXERCISE_TYPE_META[t].color,
                marginRight: 4,
              }}
            />
            {EXERCISE_TYPE_META[t].name}
          </button>
        ))}
      </div>

      {workoutsQ.isLoading ? (
        <div style={{ color: 'var(--ink-3)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: 'center',
            color: 'var(--ink-3)',
            border: '1px dashed var(--line)',
            borderRadius: 8,
            background: 'var(--surface-2)',
          }}
        >
          Nothing planned in this window. Start a training phase to materialize
          a schedule, or pick a template from the Library.
        </div>
      ) : (
        <div>
          {filtered.map((w) => (
            <div key={w.id} className={styles.histRow} onClick={() => onOpen(w)}>
              <div className={styles.histDate}>
                <div className={styles.histDay}>{formatDate(w.date)}</div>
                <div className={styles.histTime}>{relativeDay(w.date)}</div>
              </div>
              <div>
                <div className={styles.histName}>{w.name}</div>
                <div className={styles.histMeta}>
                  {isExerciseType(w.type) && <TypeChip type={w.type} />}
                  {w.durationMin && <span>{w.durationMin} min</span>}
                </div>
              </div>
              <div className={styles.histRight}>
                <StatusPill status={w.status} />
                <Icon name="arrowRight" size={12} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

function relativeDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 0) return `In ${days} days`;
  return `${-days} days ago`;
}

function isExerciseType(v: string): v is ExerciseType {
  return TYPE_KEYS.includes(v as ExerciseType);
}
